<template>
    <div class="team-page">
        <header class="top-nav">
            <button type="button" class="back-btn" @click="$router.back()">
                <van-icon name="arrow-left" />
            </button>
            <h1 class="nav-title">{{ getLabel('pageTeam.navTitle', 'Team Report') }}</h1>
        </header>

        <main class="page-content">
            <!-- 主切换：Informes / Mi Equipo -->
            <nav class="main-toggle">
                <button
                    type="button"
                    class="toggle-btn"
                    :class="{ active: mainTab === 'report' }"
                    @click="mainTab = 'report'"
                >
                    {{ getLabel('pageTeam.tabReport', 'Reports') }}
                </button>
                <button
                    type="button"
                    class="toggle-btn"
                    :class="{ active: mainTab === 'team' }"
                    @click="mainTab = 'team'"
                >
                    {{ getLabel('pageTeam.tabTeam', 'My Team') }}
                </button>
            </nav>

            <!-- 日期筛选 -->
            <div class="filter-row">
                <button type="button" class="date-picker" @click="openStart">
                    {{ startDate || getLabel('item.all', 'All') }}
                    <van-icon name="calendar-o" />
                </button>
                <span class="dash">—</span>
                <button type="button" class="date-picker" @click="openEnd">
                    {{ endDate || getLabel('item.all', 'All') }}
                    <van-icon name="calendar-o" />
                </button>
                <button type="button" class="search-btn" @click="start">
                    {{ getLabel('pageTeam.search', 'Search') }}
                </button>
            </div>

            <!-- ============ Informes Tab ============ -->
            <template v-if="mainTab === 'report'">
                <!-- 总充值卡 -->
                <section class="hero-card">
                    <div class="hero-icon">
                        <van-icon name="cash-back-record" />
                    </div>
                    <div class="hero-info">
                        <b>{{ formatCurrency(teamRecharge) }}</b>
                        <span>{{ getLabel('pageTeam.teamRecharge', 'Team Recharge') }}</span>
                    </div>
                </section>

                <!-- 2x2 指标 -->
                <section class="stat-grid">
                    <div class="stat-card green">
                        <div class="stat-icon"><van-icon name="friends-o" /></div>
                        <b>{{ teamSize }}</b>
                        <span>{{ getLabel('pageTeam.teamSize', 'Team Size') }}</span>
                    </div>
                    <div class="stat-card yellow">
                        <div class="stat-icon"><van-icon name="gold-coin-o" /></div>
                        <b>{{ firstRecharge }}</b>
                        <span>{{ getLabel('pageTeam.firstRecharge', 'First Recharge') }}</span>
                    </div>
                    <div class="stat-card orange">
                        <div class="stat-icon"><van-icon name="star-o" /></div>
                        <b>{{ directRefs }}</b>
                        <span>{{ getLabel('pageTeam.directRefs', 'Direct Referrals') }}</span>
                    </div>
                    <div class="stat-card purple">
                        <div class="stat-icon"><van-icon name="contact" /></div>
                        <b>{{ newMembers }}</b>
                        <span>{{ getLabel('pageTeam.newMembers', 'New Members') }}</span>
                    </div>
                </section>

                <!-- 按层级数据 -->
                <section class="level-card">
                    <div class="level-head">
                        <van-icon name="friends-o" />
                        <span>{{ getLabel('pageTeam.byLevel', 'Data by Level') }}</span>
                    </div>
                    <div v-for="lv in levelData" :key="lv.name" class="level-block">
                        <span class="level-badge">{{ lv.name }}</span>
                        <div class="level-row">
                            <div class="level-item">
                                <p><van-icon name="description" /> {{ getLabel('pageTeam.rechargedAmount', 'Recharged Amount') }}</p>
                                <b>{{ formatCurrency(lv.amount) }}</b>
                            </div>
                            <div class="level-item">
                                <p><van-icon name="records" /> {{ getLabel('pageTeam.rechargeCount', 'Number of Recharges') }}</p>
                                <b>{{ lv.count }}</b>
                            </div>
                        </div>
                    </div>
 
                </section>
            </template>

            <!-- ============ Mi Equipo Tab ============ -->
            <template v-else>
                <div class="chip-label">{{ getLabel('pageTeam.myTeam', 'My Team') }}</div>

                <section class="summary-row">
                    <div class="summary-cell">
                        <small>{{ getLabel('pageTeam.currentLevel', 'Current Level') }}</small>
                        <b>{{ currentLevelName }}</b>
                    </div>
                    <div class="summary-cell">
                        <small>{{ getLabel('pageTeam.members', 'Members') }}</small>
                        <b>{{ teamSize }}</b>
                    </div>
                </section>

                <section class="member-card">
                    <template v-if="members.length">
                        <div v-for="(item, index) in members" :key="index" class="member-row">
                            <van-icon name="contact" class="member-avatar" />
                            <div class="member-info">
                                <b>{{ item.phone || item.user_name || item.name || "--" }}</b>
                                <small>{{ item.levelLabel || "Level 1" }}</small>
                            </div>
                            <span class="member-time">{{ item.time || "" }}</span>
                        </div>
                    </template>
                    <div v-else class="empty-box">
                        <div class="empty-icon"><van-icon name="friends-o" /></div>
                        <p>{{ getLabel('pageTeam.emptyMembers', 'No team members') }}</p>
                    </div>
                 
                </section>
            </template>
        </main>

        <!-- 开始日期选择弹层 -->
        <van-popup v-model="showStart" position="bottom" round class="team-popup">
            <van-datetime-picker
                v-model="pickerStart"
                type="date"
                :min-date="minDate"
                :max-date="maxDate"
                :confirm-button-text="getLabel('pageTeam.confirm', 'Confirm')"
                :cancel-button-text="getLabel('pageTeam.cancel', 'Cancel')"
                class="team-picker"
                @confirm="confirmStart"
                @cancel="showStart = false"
            />
        </van-popup>

        <!-- 结束日期选择弹层 -->
        <van-popup v-model="showEnd" position="bottom" round class="team-popup">
            <van-datetime-picker
                v-model="pickerEnd"
                type="date"
                :min-date="minDate"
                :max-date="maxDate"
                :confirm-button-text="getLabel('pageTeam.confirm', 'Confirm')"
                :cancel-button-text="getLabel('pageTeam.cancel', 'Cancel')"
                class="team-picker"
                @confirm="confirmEnd"
                @cancel="showEnd = false"
            />
        </van-popup>
    </div>
</template>

<script>
import Fetch from "../../utils/fetch";
import Vue from "vue";
import { Icon, Popup, DatetimePicker } from "vant";

Vue.use(Icon);
Vue.use(Popup);
Vue.use(DatetimePicker);

export default {
    name: "myTeam",
    data() {
        return {
            mainTab: "report",
            data: {},
            members: [],
            appOriginalStyles: null,
            bodyOriginalBackground: "",
            // 日期筛选状态
            startDate: "",
            endDate: "",
            showStart: false,
            showEnd: false,
            pickerStart: new Date(),
            pickerEnd: new Date(),
            minDate: new Date(2020, 0, 1),
            maxDate: new Date(),
        };
    },
    computed: {
        teamRecharge() {
            return Number(this.data.count_recharge || 0);
        },
        teamSize() {
            const top1 = Array.isArray(this.data.top1) ? this.data.top1.length : 0;
            const top2 = Array.isArray(this.data.top2) ? this.data.top2.length : 0;
            const top3 = Array.isArray(this.data.top3) ? this.data.top3.length : 0;
            return top1 + top2 + top3;
        },
        directRefs() {
            return Array.isArray(this.data.top1) ? this.data.top1.length : 0;
        },
        firstRecharge() {
            return Number(this.data.first_recharge || this.data.first_recharge_count || 0);
        },
        newMembers() {
            return Number(this.data.new_members || this.data.new_user_count || this.data.today_new || 0);
        },
        currentLevelName() {
            if (this.data.level_name) return this.data.level_name;
            if (this.data.level) return "Level " + this.data.level;
            return "Level 1";
        },
        levelData() {
            const levels = [];
            for (let i = 1; i <= 3; i++) {
                // 按接口数据结构读取 level1~3 的 money / count
                const lv = this.data[`level${i}`] || {};
                levels.push({
                    name: "Level " + i,
                    amount: Number(lv.money ?? lv.recharge ?? lv.total_money ?? 0),
                    count: Number(lv.count ?? lv.num ?? 0),
                });
            }
            return levels;
        },
    },
    created() {
        this.$parent.footer("myTeam", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.start();
    },
    beforeDestroy() {
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        // 打开开始日期弹层
        openStart() {
            this.pickerStart = this.startDate ? this.parseDate(this.startDate) : new Date();
            this.showStart = true;
        },
        // 打开结束日期弹层
        openEnd() {
            this.pickerEnd = this.endDate ? this.parseDate(this.endDate) : new Date();
            this.showEnd = true;
        },
        // 确认开始日期
        confirmStart(value) {
            this.startDate = this.formatDate(value);
            this.showStart = false;
        },
        // 确认结束日期
        confirmEnd(value) {
            this.endDate = this.formatDate(value);
            this.showEnd = false;
        },
        // 把 "YYYY-MM-DD" 解析为 Date（本地时区）
        parseDate(str) {
            const parts = String(str).split("-");
            return new Date(Number(parts[0]), Number(parts[1] || 1) - 1, Number(parts[2] || 1));
        },
        // Date 格式化为 "YYYY-MM-DD"
        formatDate(d) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#05070f";
            app.style.paddingBottom = "0";
            document.body.style.background = "#05070f";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        async start() {
            // 携带起止日期查询团队数据（无筛选时只传空对象）
            const params = {};
            if (this.startDate) params.start_time = this.startDate;
            if (this.endDate) params.end_time = this.endDate;
            const res = await Fetch("/user/myTeam", params);
            this.data = res.data || {};
            this.members = this.buildMembers(this.data);
        },
        buildMembers(data) {
            const top1 = (data.top1 || []).map((item) => ({ ...item, levelLabel: "Level 1" }));
            const top2 = (data.top2 || []).map((item) => ({ ...item, levelLabel: "Level 2" }));
            const top3 = (data.top3 || []).map((item) => ({ ...item, levelLabel: "Level 3" }));
            return [].concat(top1, top2, top3);
        },
        formatCurrency(value) {
            return Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
    },
};
</script>

<style lang="less" scoped>
.team-page {
    min-height: 100vh;
    background: #05070f;
    color: #fff;
}

.top-nav {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 16px;
    background: rgba(5, 7, 15, 0.9);
    backdrop-filter: blur(12px);
}

.back-btn {
    position: absolute;
    left: 16px;
    top: 50%;
    width: 30px;
    height: 30px;
    margin-top: -15px;
    padding: 0;
    border: 1px solid rgba(90, 130, 200, 0.3);
    border-radius: 50%;
    background: rgba(20, 35, 65, 0.6);
    color: #cdd8ee;
    display: flex;
    align-items: center;
    justify-content: center;
}

.back-btn .van-icon { font-size: 18px; }

.nav-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #eef3ff;
}

.page-content {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 14px 14px 40px;
    box-sizing: border-box;
}

/* ---------- 主切换 ---------- */
.main-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;
}

.toggle-btn {
    height: 44px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 10px;
    background: rgba(12, 24, 46, 0.6);
    color: #8a9bb8;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.toggle-btn.active {
    background: linear-gradient(90deg, #2fd0e6, #27b6d6);
    border-color: #2fd0e6;
    color: #04222b;
    box-shadow: 0 0 16px rgba(45, 208, 230, 0.35);
}

/* ---------- 日期筛选 ---------- */
.filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
}

.date-picker {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 38px;
    padding: 0 12px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 10px;
    background: rgba(12, 24, 46, 0.6);
    color: #8a9bb8;
    font-size: 12px;
}

.date-picker .van-icon { font-size: 14px; }

.dash {
    color: #5a6b8c;
    font-size: 14px;
}

.search-btn {
    height: 38px;
    padding: 0 16px;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(90deg, #2fd0e6, #27b6d6);
    color: #04222b;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
}

/* ---------- 总充值卡 ---------- */
.hero-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 16px;
    margin-bottom: 12px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(13, 30, 56, 0.9), rgba(8, 16, 32, 0.9));
}

.hero-icon {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(45, 208, 230, 0.15);
    color: #2fd0e6;
    display: flex;
    align-items: center;
    justify-content: center;
}

.hero-icon .van-icon { font-size: 22px; }

.hero-info b {
    display: block;
    color: #fff;
    font-size: 22px;
    font-weight: 800;
}

.hero-info span {
    color: #8a9bb8;
    font-size: 12px;
}

/* ---------- 2x2 指标 ---------- */
.stat-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;
}

.stat-card {
    position: relative;
    padding: 14px;
    border-radius: 12px;
    overflow: hidden;
}

.stat-card.green {
    background: rgba(20, 60, 45, 0.5);
    border: 1px solid rgba(61, 220, 132, 0.25);
}
.stat-card.yellow {
    background: rgba(60, 50, 15, 0.5);
    border: 1px solid rgba(240, 181, 58, 0.25);
}
.stat-card.orange {
    background: rgba(60, 35, 15, 0.5);
    border: 1px solid rgba(255, 154, 46, 0.25);
}
.stat-card.purple {
    background: rgba(45, 25, 60, 0.5);
    border: 1px solid rgba(180, 100, 220, 0.25);
}

.stat-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
}

.green .stat-icon { background: rgba(61, 220, 132, 0.15); color: #3ddc84; }
.yellow .stat-icon { background: rgba(240, 181, 58, 0.15); color: #f0b53a; }
.orange .stat-icon { background: rgba(255, 154, 46, 0.15); color: #ff9a2e; }
.purple .stat-icon { background: rgba(180, 100, 220, 0.15); color: #c86de0; }

.stat-icon .van-icon { font-size: 18px; }

.stat-card b {
    display: block;
    color: #fff;
    font-size: 20px;
    font-weight: 800;
}

.stat-card span {
    color: #8a9bb8;
    font-size: 11px;
}

/* ---------- 按层级数据 ---------- */
.level-card {
    position: relative;
    padding: 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: rgba(10, 20, 40, 0.7);
    overflow: hidden;
}

.level-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    color: #e6edfb;
    font-size: 14px;
    font-weight: 700;
}

.level-head .van-icon { color: #2fd0e6; font-size: 16px; }

.level-block {
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid rgba(70, 110, 190, 0.2);
    border-radius: 10px;
    background: rgba(8, 16, 32, 0.6);
}

.level-block:last-child { margin-bottom: 0; }

.level-badge {
    display: inline-block;
    padding: 3px 10px;
    margin-bottom: 10px;
    border-radius: 6px;
    background: rgba(45, 208, 230, 0.15);
    color: #2fd0e6;
    font-size: 11px;
    font-weight: 700;
}

.level-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.level-item p {
    margin: 0 0 6px;
    color: #8a9bb8;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.level-item p .van-icon { font-size: 12px; }

.level-item b {
    color: #fff;
    font-size: 16px;
    font-weight: 800;
}

/* ---------- Mi Equipo ---------- */
.chip-label {
    display: inline-block;
    padding: 6px 14px;
    margin-bottom: 12px;
    border-radius: 8px;
    background: rgba(45, 208, 230, 0.15);
    color: #2fd0e6;
    font-size: 12px;
    font-weight: 700;
}

.summary-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 16px;
    margin-bottom: 12px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: rgba(10, 20, 40, 0.7);
}

.summary-cell small {
    display: block;
    margin-bottom: 6px;
    color: #8a9bb8;
    font-size: 11px;
}

.summary-cell b {
    color: #fff;
    font-size: 18px;
    font-weight: 800;
}

.member-card {
    position: relative;
    padding: 16px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: rgba(10, 20, 40, 0.7);
    overflow: hidden;
    min-height: 180px;
}

.member-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(70, 110, 190, 0.15);
}

.member-avatar {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(45, 208, 230, 0.12);
    color: #2fd0e6;
    display: flex;
    align-items: center;
    justify-content: center;
}

.member-info {
    flex: 1;
    min-width: 0;
}

.member-info b {
    display: block;
    color: #fff;
    font-size: 14px;
}

.member-info small {
    color: #2fd0e6;
    font-size: 11px;
}

.member-time {
    color: #6b7d9c;
    font-size: 11px;
}

.empty-box {
    padding: 40px 0;
    text-align: center;
}

.empty-icon {
    width: 60px;
    height: 60px;
    margin: 0 auto 14px;
    border-radius: 14px;
    background: rgba(45, 208, 230, 0.08);
    color: rgba(45, 208, 230, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-icon .van-icon { font-size: 30px; }

.empty-box p {
    margin: 0;
    color: #6b7d9c;
    font-size: 13px;
}

/* ---------- 日期选择弹层（深色适配） ---------- */
.team-popup {
    background: #0e1830;
}

.team-picker {
    background: #0e1830;
}

.team-picker ::v-deep .van-picker__toolbar {
    background: #0e1830;
}

.team-picker ::v-deep .van-picker__cancel,
.team-picker ::v-deep .van-picker__confirm {
    color: #2fd0e6;
}

.team-picker ::v-deep .van-picker__title {
    color: #e6edfb;
}

.team-picker ::v-deep .van-picker-column__item {
    color: #8a9bb8;
}

.team-picker ::v-deep .van-picker-column__item--selected {
    color: #2fd0e6;
}

/* ---------- 装饰 ---------- */
.deco-avatar {
    position: absolute;
    right: 14px;
    top: 14px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.2);
    background: radial-gradient(circle at 35% 30%, #ffd0a0, #c45a8a 50%, #4a1a4a);
    opacity: 0.85;
}

.deco-wheel {
    position: absolute;
    right: 46px;
    top: 18px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: conic-gradient(
        #ffd25e 0 30deg, #ff6b6b 30deg 60deg,
        #4ecdc4 60deg 90deg, #6c5ce7 90deg 120deg,
        #ffd25e 120deg 150deg, #ff6b6b 150deg 180deg,
        #4ecdc4 180deg 210deg, #6c5ce7 210deg 240deg,
        #ffd25e 240deg 270deg, #ff6b6b 270deg 300deg,
        #4ecdc4 300deg 330deg, #6c5ce7 330deg 360deg
    );
    opacity: 0.85;
}
</style>
